"use client";

import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Loader2, Edit3, GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DiffView } from "@/components/ui/diff-view";
import { toast } from "sonner";
import { analyticsResource } from "@/lib/analytics";

interface ChangeRequestFormProps {
  resourceId: string;
  currentContent: string;
  currentTitle: string;
  resourceType?: string;
}

export function ChangeRequestForm({ resourceId, currentContent, currentTitle }: ChangeRequestFormProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [proposedContent, setProposedContent] = useState(currentContent);
  const [proposedTitle, setProposedTitle] = useState(currentTitle);
  const [reason, setReason] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasContentChanges = proposedContent !== currentContent;
  const hasTitleChanges = proposedTitle !== currentTitle;
  const hasChanges = hasContentChanges || hasTitleChanges;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasChanges) {
      toast.error(t("changeRequests.mustMakeChanges"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`/api/resources/${resourceId}/changes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          proposedContent,
          proposedTitle: hasTitleChanges ? proposedTitle : undefined,
          reason: reason || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create change request");
      }

      const result = await response.json();
      analyticsResource.changeRequest(resourceId, "create");
      toast.success(t("changeRequests.created"));
      router.push(`/registry/${resourceId}/changes/${result.id}`);
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("common.somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title input */}
      <div className="space-y-2">
        <Label htmlFor="proposedTitle">{t("changeRequests.proposedTitle")}</Label>
        <Input
          id="proposedTitle"
          value={proposedTitle}
          onChange={(e) => setProposedTitle(e.target.value)}
          placeholder={currentTitle}
        />
        {hasTitleChanges && (
          <p className="text-xs text-muted-foreground">
            <span className="text-red-600 dark:text-red-400 line-through">{currentTitle}</span>
            {" → "}
            <span className="text-green-600 dark:text-green-400">{proposedTitle}</span>
          </p>
        )}
      </div>

      {/* Description with tabs */}
      <div className="space-y-2">
        <Label>{t("changeRequests.proposedContent")}</Label>
        <Tabs defaultValue="edit">
          <TabsList className="grid w-full grid-cols-2 mb-2">
            <TabsTrigger value="edit" className="gap-1.5">
              <Edit3 className="h-3.5 w-3.5" />
              {t("changeRequests.edit")}
            </TabsTrigger>
            <TabsTrigger value="diff" className="gap-1.5">
              <GitCompare className="h-3.5 w-3.5" />
              {t("changeRequests.preview")}
              {hasContentChanges && (
                <span className="ml-1 h-4 min-w-4 px-1 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">✓</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="mt-0">
            <div className="border rounded-lg overflow-hidden">
              <Textarea
                ref={textareaRef}
                id="proposedContent"
                value={proposedContent}
                onChange={(e) => setProposedContent(e.target.value)}
                placeholder={t("changeRequests.proposedContentPlaceholder")}
                className="min-h-[300px] font-mono text-sm border-0 rounded-none focus-visible:ring-0"
                required
              />
            </div>
          </TabsContent>

          <TabsContent value="diff" className="mt-0">
            {hasContentChanges ? (
              <DiffView
                original={currentContent}
                modified={proposedContent}
                className="min-h-[300px]"
              />
            ) : (
              <div className="min-h-[300px] flex items-center justify-center border rounded-lg bg-muted/30">
                <div className="text-center">
                  <GitCompare className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t("changeRequests.noChangesYet")}</p>
                  <p className="text-xs text-muted-foreground mt-1">Edit the description to see changes</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Reason */}
      <div className="space-y-2">
        <Label htmlFor="reason">{t("changeRequests.reason")}</Label>
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("changeRequests.reasonPlaceholder")}
          className="min-h-[80px]"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="text-sm text-muted-foreground">
          {hasChanges ? (
            <span className="text-green-600 dark:text-green-400 flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              {t("changeRequests.changesDetected")}
            </span>
          ) : (
            <span>{t("changeRequests.noChangesYet")}</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => router.back()}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !hasChanges}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("changeRequests.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
